import React from 'lib/react';
import { EVGrid } from 'lib/components';
import Typography from 'commonComponents/typography';
import { useTranslation } from 'lib/translation';
import AnalyticsBarChart from 'commonComponents/analyticsBarChart';
import AnalyticsPieChart from 'commonComponents/analyticsPieChart';
import { useSelector } from 'lib/redux';
import {
  PENDING_REASONS_COLORS, PENDING_REASONS_LABLES, STATUS_COLORS, STATUS_LABELS,
} from '../Analytics.constants';
import LastUpdatedTime from './LastUpdatedTime.component';
import BarChartLegend from './BarChartLegend.component';
import ChartsStyles from './ChartsStyles';
import PieChartLegend from './PieChartLegend.component';
import { percentageValueFormatter } from '../Analytics.utils';

function StatusCharts() {
  const classes = ChartsStyles();
  const reportAnalytics = useSelector(({ analyticsDashboard }) => analyticsDashboard.reportAnalytics);
  const { t } = useTranslation();

  const reportsStatusData = Object.entries(reportAnalytics.reduce(
    (acc, item) => {
      const month = new Date(item.orderPlacedDate).getMonth();
      const monthData = acc[month] || { };
      const status = item.currentStatus;
      const statusCount = monthData[status] || 0;
      return { ...acc, [month]: { ...monthData, [status]: statusCount + 1 } };
    },
    {},
  )).map(([month, monthData]) => ({ ...monthData, month: Number(month) + 1 }));

  const statusBarChartSeries = Object.entries(STATUS_COLORS).map(([status, color]) => ({
    dataKey: status,
    stack: 'reportsCount',
    color,
    label: STATUS_LABELS[status],
    valueFormatter: (value) => `${value} Reports`,
  }));

  const statusPieChartSeriesData = Object.entries(reportsStatusData.reduce((acc, item) => {
    const {
      Completed, Closed, InProgress, Pending,
    } = item;
    return {
      ...acc,
      Completed: (acc.Completed || 0) + (Completed || 0),
      Pending: (acc.Pending || 0) + (Pending || 0),
      Closed: (acc.Closed || 0) + (Closed || 0),
      InProgress: (acc.InProgress || 0) + (InProgress || 0),
    };
  }, {})).map(([status, value]) => ({
    label: STATUS_LABELS[status], value, color: STATUS_COLORS[status], total: reportAnalytics.length,
  }));

  const { total: pendingReasonsTotalCount, ...pendingReasonsPieChartSeries } = reportAnalytics.reduce((acc, item) => {
    const {
      currentStatus, subStatus,
    } = item;

    if (currentStatus !== 'Pending') return acc;
    return {
      ...acc,
      [subStatus]: (acc[subStatus] || 0) + 1,
      total: (acc.total || 0) + 1,
    };
  }, {});

  const pendingReasonsPieChartSeriesData = Object.entries(pendingReasonsPieChartSeries)
    .map(([subStatus, value]) => ({
      label: PENDING_REASONS_LABLES[subStatus], value, color: PENDING_REASONS_COLORS[subStatus], total: pendingReasonsTotalCount,
    }));

  return (
    <>
      <EVGrid className={classes.barChartContainer}>
        <Typography variant="h3-600" fontColor="charcoal-900">{t('analytics_dashboard__status_of_orders')}</Typography>
        <EVGrid className={classes.barChartDiv}>
          <AnalyticsBarChart
            dataset={reportsStatusData}
            series={statusBarChartSeries}
            xAxis={[{
              scaleType: 'band',
              dataKey: 'month',
              categoryGapRatio: 0.05,
              disableTicks: true,
              valueFormatter: (monthNum) => new Date(2021, monthNum - 1).toLocaleString('default', { month: 'short' }).toUpperCase(),
            }]}
            yAxis={[{
              disableTicks: true,
              label: t('analytics_dashboard__number_of_products'),
            }]}
          />
        </EVGrid>
        <EVGrid className={classes.legend}>
          <BarChartLegend lengendData={Object.entries(STATUS_COLORS).map(([status, color]) => ({ label: STATUS_LABELS[status], color }))} />
          <LastUpdatedTime />
        </EVGrid>
      </EVGrid>
      <EVGrid className={classes.pieChartsContainer}>
        <EVGrid className={classes.pieChartContainer}>
          <Typography variant="body-400" fontColor="charcoal-900">{t('common__status')}</Typography>
          <EVGrid className={classes.pieChartDiv}>
            <AnalyticsPieChart
              series={[{ data: statusPieChartSeriesData, valueFormatter: percentageValueFormatter }]}
              height={103}
              width={103}
            />
            <PieChartLegend lengendData={statusPieChartSeriesData} />
          </EVGrid>
        </EVGrid>
        {(pendingReasonsPieChartSeriesData?.length > 0) && (
          <EVGrid className={classes.pieChartContainer}>
            <Typography variant="body-400" fontColor="charcoal-900">{t('analytics_dashboard__pending_reasons')}</Typography>
            <EVGrid className={classes.pieChartDiv}>
              <AnalyticsPieChart
                series={[{
                  data: pendingReasonsPieChartSeriesData,
                  valueFormatter: percentageValueFormatter,
                }]}
                height={103}
                width={103}
              />
              <PieChartLegend lengendData={pendingReasonsPieChartSeriesData} />
            </EVGrid>
          </EVGrid>
        )}
      </EVGrid>
    </>
  );
}

export default StatusCharts;
